/* The boot screen inlined in index.html.

   It exists so the first paint is the brand rather than a white rectangle, and
   so a failure before React mounts has somewhere to say so. Both functions are
   defensive about the node being absent — a stale index.html from a previous
   deploy should not take down a working bundle. */

/** Longest the fade is given before the node is removed regardless. */
const REMOVE_AFTER_MS = 600

export function dismissBoot(): void {
  const boot = document.getElementById('boot')
  if (!boot) return

  /* The removal is scheduled here, on a timer, and *not* inside the animation
     frame below.

     It used to be inside it, which meant the belt-and-braces removal was
     nested in the thing it was bracing against: `requestAnimationFrame` does
     not run while the page is hidden, so a tab restored in the background — a
     phone waking onto a session Chrome reloaded, a link opened in a background
     tab — never scheduled the fallback at all. The boot screen is a fixed,
     opaque, z-index 9999 sheet over the whole app, so the cost of that was the
     entire screen, indefinitely.

     A timer fires in a hidden page (throttled, which is fine — nobody is
     looking), so this path always completes. */
  const removal = window.setTimeout(() => boot.remove(), REMOVE_AFTER_MS)

  /* Faded out rather than removed on the spot, and only after a frame, so the
     handover from the boot mark to the rendered header is a cross-fade instead
     of a flash of unstyled layout. */
  requestAnimationFrame(() => {
    boot.classList.add('is-gone')
    boot.addEventListener(
      'transitionend',
      () => {
        window.clearTimeout(removal)
        boot.remove()
      },
      { once: true },
    )
  })
}

/* Spelled out here rather than pulled from the locale bundles, because the one
   call site is the branch where loading those bundles is what failed. A
   translation table that lives inside the thing that did not load cannot
   report that it did not load. Read straight from storage for the same
   reason — i18next may never have initialised. */
const FAILURE: Record<string, string> = {
  en: 'HexClean could not start. Check your connection and reload.',
  ar: 'تعذّر تشغيل هكس كلين. تحقّق من اتصالك وأعد التحميل.',
}

function storedLocale(): string {
  try {
    return localStorage.getItem('hexclean:v1:locale') ?? 'en'
  } catch {
    return 'en'
  }
}

export function showBootFailure(message?: string): void {
  const boot = document.getElementById('boot')
  if (!boot) return
  const locale = storedLocale()
  const slot = boot.querySelector('[data-boot-message]')
  if (slot) {
    slot.textContent = message ?? FAILURE[locale] ?? FAILURE.en!
    if (!message) {
      slot.setAttribute('lang', locale in FAILURE ? locale : 'en')
      slot.setAttribute('dir', locale === 'ar' ? 'rtl' : 'ltr')
    }
  }
  boot.classList.add('is-failed')
}
